import { useEffect, useState, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  FolderOpen,
  Microscope,
  ShieldCheck,
  Settings2,
  Search,
  Bookmark,
  Scale,
  BookOpen,
  ListChecks,
  GitCommitHorizontal,
  AlertTriangle,
  Network,
  Gavel,
  EyeOff,
  MessageSquareQuote,
  Clock,
  UserCheck,
  Sparkles,
  LogOut,
  Activity,
  Building2,
} from 'lucide-react';
import { cn } from '@/shared/cn';
import { api } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { canAccessTab, tabForPath, TabId } from '@/shared/roles';
import CommandPalette from '@/components/CommandPalette';
import JobsDock from '@/components/JobsDock';
import NotificationDropdown from '@/components/NotificationDropdown';
import { oidcManager, ssoLogout } from '@/shared/oidc';

interface Tab {
  id: string;
  path: string;
  roman: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const TABS: Tab[] = [
  { id: 'dashboard', path: '/dashboard', roman: 'I',   label: 'Dashboard',     icon: LayoutDashboard },
  { id: 'berkas',    path: '/berkas',    roman: 'II',  label: 'Berkas',        icon: FolderOpen },
  { id: 'analisa',   path: '/analisa',   roman: 'III', label: 'Analisa',       icon: Microscope },
  { id: 'audit',     path: '/audit',     roman: 'IV',  label: 'Audit',         icon: ShieldCheck },
  { id: 'admin',     path: '/admin',     roman: 'V',   label: 'Administrasi',  icon: Settings2 },
  { id: 'privacy',   path: '/privacy',   roman: 'VI',  label: 'Privacy',       icon: EyeOff },
];

interface DrawerSection {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  meta: string;
  path?: string;
}

function isDrawerItemActive(
  itemPath: string | undefined,
  pathname: string,
  search: string,
  hash: string,
): boolean {
  if (!itemPath) return false;
  const [pathPart, query] = itemPath.split('?');
  const [pathOnly, hashPart] = pathPart.split('#');
  if (pathOnly !== pathname) {
    // Path beda → tidak active
    if (!pathOnly && (pathname + '').endsWith(pathPart)) {
      // edge case path relatif
    } else {
      return false;
    }
  }
  // Compare query params subset: each itemPath query param must exist in current search
  if (query) {
    const itemParams = new URLSearchParams(query);
    const currentParams = new URLSearchParams(search);
    for (const [k, v] of itemParams.entries()) {
      if (currentParams.get(k) !== v) return false;
    }
    return true;
  }
  // Compare hash if itemPath has hash
  if (hashPart) {
    return hash === `#${hashPart}` || hash === hashPart;
  }
  // No query/hash → match plain pathname (and current pathname must not have ?view= or filter params that contradict)
  return search === '' || search === '?';
}

const DRAWER_SECTIONS_BY_TAB: Record<string, DrawerSection[]> = {
  dashboard: [
    { id: 'kpi',       icon: LayoutDashboard, title: 'KPI Eksekutif',     desc: 'Total BAP, temuan, pasal indikatif, kontradiksi, kronologi.', meta: '5 metrik',    path: '/dashboard#kpi' },
    { id: 'trend',     icon: Activity,        title: 'Tren Unggah',       desc: 'Sparkline 30 hari volume BAP masuk per satker.',              meta: '30 hari',     path: '/dashboard#trend' },
    { id: 'top-pasal', icon: Gavel,           title: 'Top Pasal',         desc: 'Ranking pasal indikatif paling sering muncul lintas BAP.',     meta: 'Top 8',       path: '/dashboard#top-pasal' },
    { id: 'kategori',  icon: ListChecks,      title: 'Distribusi Temuan', desc: 'Breakdown kategori temuan + rata-rata severity.',              meta: '5 kategori',  path: '/dashboard#kategori' },
    { id: 'sev',       icon: AlertTriangle,   title: 'Severity Bucket',   desc: 'Histogram severity findings 0-10 dalam 5 bucket.',             meta: 'Histogram',   path: '/dashboard#severity' },
    { id: 'satker',    icon: Building2,       title: 'Distribusi Satker', desc: 'Volume BAP + halaman per satker terdaftar.',                   meta: 'Top 8',       path: '/dashboard#satker' },
    { id: 'recent',    icon: FolderOpen,      title: 'BAP Terbaru',       desc: 'Daftar 10 BAP terakhir diunggah dengan status & klasifikasi.', meta: 'Stream',      path: '/dashboard#recent' },
    { id: 'audit-now', icon: ShieldCheck,     title: 'Audit Hari Ini',    desc: 'Hitung audit event hari ini + status hash-chain integrity.',  meta: 'Live ✓',      path: '/audit' },
  ],
  berkas: [
    { id: 'all',     icon: FolderOpen,  title: 'Semua Berkas',         desc: 'Daftar lengkap BAP yang sudah ter-ingest ke sistem.',          meta: 'All',       path: '/berkas' },
    { id: 'ready',   icon: ListChecks,  title: 'Status: READY',        desc: 'BAP yang OCR & chunk-nya selesai dan siap dianalisa.',         meta: 'Filter',    path: '/berkas?status=READY' },
    { id: 'pending', icon: Clock,       title: 'Status: PENDING',      desc: 'BAP yang masih dalam antrian OCR / indexing.',                 meta: 'Filter',    path: '/berkas?status=PENDING' },
    { id: 'rahasia', icon: ShieldCheck, title: 'Klasifikasi: RAHASIA', desc: 'BAP berklasifikasi RAHASIA / SANGAT RAHASIA.',                 meta: 'BSSN',      path: '/berkas?classification=RAHASIA' },
    { id: 'upload',  icon: Bookmark,    title: 'Unggah BAP Baru',      desc: 'Drop PDF/DOCX ke sistem untuk ingest pipeline.',               meta: 'Drag&drop', path: '/berkas/upload' },
    { id: 'pinned',  icon: Bookmark,    title: 'Pinned',               desc: 'BAP yang Anda tandai untuk akses cepat.',                      meta: 'Personal',  path: '/berkas?pinned=1' },
  ],
  analisa: [
    { id: 'search',   icon: Microscope,         title: 'Cari Lintas BAP',    desc: 'Semantic search di semua BAP indexed dengan snippet + citation.',     meta: 'FTS · cross-doc',  path: '/analisa?view=search' },
    { id: 'entities', icon: Network,            title: 'Eksplorasi Entitas', desc: 'Jaringan entitas + mentions lintas BAP per nama/orang/organisasi.', meta: 'NER · graph',      path: '/analisa?view=entities' },
    { id: 'perkara',  icon: FolderOpen,         title: 'Perkara',            desc: 'Group BAP by case_id + agregat findings/violations/inconsistencies.',meta: 'Cluster',          path: '/analisa?view=perkara' },
    { id: 'heatmap',  icon: Gavel,              title: 'Heatmap Pasal',      desc: 'Matrix pasal indikatif × satker dengan intensity coloring.',        meta: 'KB · INDIKATIF',   path: '/analisa?view=heatmap' },
    { id: 'tanya',    icon: MessageSquareQuote, title: 'Tanya Asisten',      desc: 'Q&A chat multi-turn berbasis konteks BAP dengan citation.',         meta: 'RAG · multi-turn', path: '/tanya' },
  ],
  audit: [
    { id: 'all',     icon: ShieldCheck, title: 'Semua Event',  desc: 'Stream audit event hash-chained kronologis.',          meta: 'Live',       path: '/audit' },
    { id: 'login',   icon: UserCheck,   title: 'Login',        desc: 'Aktivitas otentikasi (DEV_LOGIN, LOGOUT, gagal).',     meta: 'Auth',       path: '/audit?event_type=DEV_LOGIN' },
    { id: 'upload',  icon: FolderOpen,  title: 'Upload',       desc: 'Event ingest BAP dan dokumen masuk.',                   meta: 'Ingest',     path: '/audit?event_type=BAP_UPLOAD' },
    { id: 'read',    icon: BookOpen,    title: 'BAP Dibaca',   desc: 'Event akses dokumen BAP_READ.',                         meta: 'Access',     path: '/audit?event_type=BAP_READ' },
    { id: 'scan',    icon: Sparkles,    title: 'AI Scan',      desc: 'FINDINGS / VIOLATION / TIMELINE / ENTITIES extraction.', meta: 'AI ops',     path: '/audit?event_type=FINDINGS_EXTRACTED' },
    { id: 'export',  icon: Bookmark,    title: 'Ekspor',       desc: 'Aktivitas unduh laporan + akses data sensitif.',        meta: 'DLP',        path: '/audit?event_type=REPORT_EXPORTED' },
    { id: 'admin',   icon: Settings2,   title: 'Administrasi', desc: 'USER_CREATED / USER_UPDATED / REGULATION_*.',           meta: 'Privileged', path: '/audit?event_type=USER_UPDATED' },
    { id: 'denied',  icon: Scale,       title: 'Denied/Error', desc: 'Aktivitas ditolak atau error sistem.',                  meta: 'Forensik',   path: '/audit?result=DENIED' },
  ],
  admin: [
    { id: 'users',      icon: UserCheck,    title: 'Pengguna',          desc: 'CRUD user, role assignment, satker, max clearance.',          meta: 'RBAC',           path: '/admin?view=users' },
    { id: 'roles',      icon: ShieldCheck,  title: 'Roles & Permission', desc: 'Matrix role × menu — atur akses navigasi tiap role.',         meta: 'RBAC · CRUD',    path: '/admin?view=roles' },
    { id: 'regulasi',   icon: Gavel,        title: 'Knowledge Base UU', desc: 'Daftar pasal indikatif + unsur untuk Violation Checking.',    meta: 'KB · CRUD',      path: '/admin?view=regulations' },
    { id: 'satker',     icon: Building2,    title: 'Satker',            desc: 'Daftar satuan kerja organisasi (Polri, Kejaksaan, KPK, dll).',meta: 'Master',         path: '/admin?view=satkers' },
    { id: 'opa',        icon: ShieldCheck,  title: 'Kebijakan OPA',     desc: 'ABAC policies Rego — siapa boleh apa atas data apa.',         meta: 'Policy-as-code', path: '/admin?view=opa' },
    { id: 'audit-conf', icon: Activity,     title: 'Audit Settings',    desc: 'Retensi event, schedule export, alerting threshold.',         meta: 'Compliance',     path: '/admin?view=audit-settings' },
    { id: 'feature',    icon: Sparkles,     title: 'Feature Flags',     desc: 'Toggle modul AI per environment, A/B prompt versions.',       meta: 'Ops',            path: '/admin?view=feature-flags' },
    { id: 'system',     icon: Settings2,    title: 'Sistem',            desc: 'Quota LLM, retention chunks, MinIO/OpenSearch health.',       meta: 'Infra',          path: '/admin?view=system' },
  ],
  privacy: [
    { id: 'rules',  icon: ListChecks, title: 'Aturan PII',        desc: 'Regex + LLM rules masking. System rules toggle, custom rules CRUD.', meta: 'CRUD · live',  path: '/privacy?view=rules' },
    { id: 'stats',  icon: Activity,   title: 'Statistik & Cakupan', desc: 'Total deteksi, breakdown label, coverage %, rules aktif.',          meta: 'Live · 30s',   path: '/privacy?view=stats' },
  ],
};

export default function AppShell() {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [scrolled, setScrolled] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Live BAP count for the Berkas tab badge.
  const { data: docStats } = useQuery<{ total: number; ready: number; processing: number }>({
    queryKey: ['documents', 'stats'],
    queryFn: () => api('/api/v1/documents/stats/summary'),
    refetchInterval: 30000,
  });
  const tabCounts: Record<string, number | undefined> = { berkas: docStats?.total };

  const userInitials = (user?.full_name || user?.email || '?')
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  const handleLogout = useCallback(async () => {
    // Cek OIDC session → trigger SSO logout (KC end_session redirect)
    try {
      const oidcUser = await oidcManager.getUser();
      if (oidcUser && !oidcUser.expired) {
        logout();
        await ssoLogout();
        return;
      }
    } catch { /* fallback */ }
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let ticking = false;
    const handler = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 4);
        ticking = false;
      });
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const switchTab = (tab: Tab) => {
    navigate(tab.path);
  };

  const visibleTabs = TABS.filter((t) => canAccessTab(user?.allowed_tabs, t.id as TabId));
  const resolvedTabId = tabForPath(location.pathname);
  const currentTab =
    (resolvedTabId && visibleTabs.find((t) => t.id === resolvedTabId)) ||
    visibleTabs.find((t) => location.pathname.startsWith(t.path)) ||
    visibleTabs[0] ||
    TABS[2];
  const drawerSections = DRAWER_SECTIONS_BY_TAB[currentTab.id] || [];
  const hasDrawer = drawerSections.length > 0;

  return (
    <div className="app-shell">
      {/* ─── TOPBAR ─── */}
      <header className={cn('topbar', scrolled && 'scrolled')}>
        <div className="brand">
          <div className="seal">
            <Scale />
          </div>
          <div>
            <div className="name">NETRA</div>
            <div className="sub">Mata Cerdas Berkas Perkara</div>
          </div>
        </div>

        <button className="search" title="Command palette (⌘K)" onClick={() => setPaletteOpen(true)}>
          <Search />
          <span>Cari berkas, perintah, tanya AI...</span>
          <kbd>⌘K</kbd>
        </button>

        <button
          onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
          className="icon-btn theme-toggle"
          title="Ganti tema"
        >
          {theme === 'dark' ? 'DARK' : 'LIGHT'}
        </button>

        <NotificationDropdown />

        <div className="user-tag">
          <div className="meta" style={{ display: window.matchMedia('(min-width: 768px)').matches ? 'block' : 'none' }}>
            <div className="name">{user?.full_name || user?.email || '—'}</div>
            <div className="role">{user?.role || '—'} · {user?.satker || '—'}</div>
          </div>
          <span className="id" title={user?.email || ''}>{userInitials || '?'}</span>
          <button onClick={handleLogout} className="icon-btn danger" title="Logout">
            <LogOut />
          </button>
        </div>
      </header>

      {/* ─── TAB BAR ─── */}
      <nav className={cn('tabbar', scrolled && 'scrolled')} role="tablist">
        {visibleTabs.map((tab) => {
          const isActive = currentTab.id === tab.id;
          const Icon = tab.icon;
          const count = tabCounts[tab.id];
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => switchTab(tab)}
              className={cn('tab', isActive && 'active')}
            >
              <span className="clip" />
              <span className="roman">{tab.roman}</span>
              <Icon />
              {tab.label}
              {count !== undefined && <span className="count">{count}</span>}
            </button>
          );
        })}

        <div className="tabbar-spacer" />

        <div className="tabbar-right">
          <button className="icon-btn" title="Bookmark">
            <Bookmark />
          </button>
        </div>
      </nav>

      {/* ─── DRAWER (mini only) ─── */}
      <div className={cn('drawer', !hasDrawer ? 'off' : 'mini', scrolled && 'scrolled')}>
        {hasDrawer && (
          <div className="mini-mode">
            <span className="mini-strip-label">— {currentTab.label}</span>
            {drawerSections.map((s) => {
              const Icon = s.icon;
              const active = isDrawerItemActive(s.path, location.pathname, location.search, location.hash);
              return (
                <button
                  key={s.id}
                  onClick={() => s.path && navigate(s.path)}
                  className={cn('mini-item', active && 'active')}
                >
                  <Icon />
                  {s.title}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── CRUMB BAR (kontekstual) ─── */}
      <div className={cn('crumb-bar', scrolled && 'scrolled')}>
        <span>{currentTab.label}</span>
        {(() => {
          // Detect /berkas/:id → show BAP id snippet
          const m = location.pathname.match(/^\/berkas\/([^/]+)/);
          if (m && m[1] !== 'upload') {
            return (
              <>
                <span className="sep">/</span>
                <span className="current">BAP · {m[1].slice(0, 8)}</span>
              </>
            );
          }
          // Read ?view= for active sub-view (admin/analisa/berkas-detail)
          const view = new URLSearchParams(location.search).get('view');
          if (view) {
            return (
              <>
                <span className="sep">/</span>
                <span className="current">{view.replace(/-/g, ' ').toUpperCase()}</span>
              </>
            );
          }
          if (location.pathname === '/berkas/upload') {
            return (
              <>
                <span className="sep">/</span>
                <span className="current">UPLOAD</span>
              </>
            );
          }
          return null;
        })()}
        <div className="right">
          <span>
            <UserCheck />
            {user?.full_name || user?.email || '—'}
          </span>
          <span>
            <Clock />
            {new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        </div>
      </div>

      {/* ─── CONTENT ─── */}
      <main className="px-6 py-10 max-w-[1680px] mx-auto">
        <Outlet />
      </main>

      <footer className="mt-12 py-4 px-8 text-center font-mono text-[9px] tracking-widest text-ink-3 border-t border-ink-line">
        ASISTEN AI BERSIFAT INDIKATIF · PENETAPAN TERSANGKA, PASAL, DAN TINDAK LANJUT ADALAH
        WEWENANG PENYIDIK DAN JPU
      </footer>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <JobsDock />
    </div>
  );
}
